import { createBundlerClient, getEntryPoint, InvalidEntityIdError, InvalidNonceKeyError, InvalidDeferredActionNonce, toSmartContractAccount, } from "@aa-sdk/core";
import { DEFAULT_OWNER_ENTITY_ID, parseDeferredAction } from "../../utils.js";
import { encodeFunctionData, maxUint32, zeroAddress, getContract, concatHex, maxUint152, } from "viem";
import { modularAccountAbi } from "../../abis/modularAccountAbi.js";
import { serializeModuleEntity } from "../../actions/common/utils.js";
import { nativeSMASigner } from "../nativeSMASigner.js";
import { singleSignerMessageSigner } from "../../modules/single-signer-validation/signer.js";
export const executeUserOpSelector = "0x8DD7712F";
export async function createMAv2Base(config) {
    let { transport, chain, signer, entryPoint = getEntryPoint(chain, { version: "0.7.0" }), signerEntity = {
        isGlobalValidation: true,
        entityId: DEFAULT_OWNER_ENTITY_ID,
    }, signerEntity: { isGlobalValidation = true, entityId = DEFAULT_OWNER_ENTITY_ID, } = {}, accountAddress, deferredAction, ...remainingToSmartContractAccountParams } = config;
    if (entityId > Number(maxUint32)) {
        throw new InvalidEntityIdError(entityId);
    }
    const client = createBundlerClient({
        transport,
        chain,
    });
    const entryPointContract = getContract({
        address: entryPoint.address,
        abi: entryPoint.abi,
        client,
    });
    // These default values signal that we should not use the set deferred action nonce
    let nonce;
    let deferredActionData;
    let hasAssociatedExecHooks = false;
    if (deferredAction) {
        let deferredActionNonce = 0n;
        // We always update entity id and isGlobalValidation to the deferred action value since the client could be used to send multiple calls
        ({
            entityId,
            isGlobalValidation,
            nonce: deferredActionNonce,
        } = parseDeferredAction(deferredAction));
        // Set these values if the deferred action has not been consumed. We check this with the EP
        const nextNonceForDeferredAction = (await entryPointContract.read.getNonce([
            accountAddress,
            deferredActionNonce >> 64n,
        ]));
        if (deferredActionNonce === nextNonceForDeferredAction) {
            ({ nonce, deferredActionData, hasAssociatedExecHooks } =
                parseDeferredAction(deferredAction));
        }
        else if (deferredActionNonce > nextNonceForDeferredAction) {
            // if nonce is greater than the next nonce, its invalid, so we throw
            throw new InvalidDeferredActionNonce();
        }
    }
    const encodeExecute = async ({ target, data, value, }) => await encodeCallData(encodeFunctionData({
        abi: modularAccountAbi,
        functionName: "execute",
        args: [target, value ?? 0n, data],
    }));
    const encodeBatchExecute = async (txs) => await encodeCallData(encodeFunctionData({
        abi: modularAccountAbi,
        functionName: "executeBatch",
        args: [
            txs.map((tx) => ({
                target: tx.target,
                data: tx.data,
                value: tx.value ?? 0n,
            })),
        ],
    }));
    const isAccountDeployed = async () => !!(await client.getCode({ address: accountAddress }));
    const getNonce = async (nonceKey = 0n) => {
        if (nonce) {
            const tempNonce = nonce;
            nonce = undefined; // set to falsy value once used
            return tempNonce;
        }
        if (nonceKey > maxUint152) {
            throw new InvalidNonceKeyError(nonceKey);
        }
        const fullNonceKey = (nonceKey << 40n) +
            (BigInt(entityId) << 8n) +
            (isGlobalValidation ? 1n : 0n);
        return entryPointContract.read.getNonce([
            accountAddress,
            fullNonceKey,
        ]);
    };
    const accountContract = getContract({
        address: accountAddress,
        abi: modularAccountAbi,
        client,
    });
    const getExecutionData = async (selector) => {
        if (!(await isAccountDeployed())) {
            return {
                module: zeroAddress,
                skipRuntimeValidation: false,
                allowGlobalValidation: false,
                executionHooks: [],
            };
        }
        return await accountContract.read.getExecutionData([selector]);
    };
    const getValidationData = async (args) => {
        if (!(await isAccountDeployed())) {
            return {
                validationHooks: [],
                executionHooks: [],
                selectors: [],
                validationFlags: 0,
            };
        }
        const { validationModuleAddress, entityId } = args;
        return await accountContract.read.getValidationData([
            serializeModuleEntity({
                moduleAddress: validationModuleAddress ?? zeroAddress,
                entityId: entityId ?? Number(maxUint32),
            }),
        ]);
    };
    const encodeCallData = async (callData) => {
        const validationData = await getValidationData({
            entityId: Number(entityId),
        });
        if (hasAssociatedExecHooks) {
            hasAssociatedExecHooks = false; // set to falsy value once used
            return concatHex([executeUserOpSelector, callData]);
        }
        if (validationData.executionHooks.length) {
            return concatHex([executeUserOpSelector, callData]);
        }
        return callData;
    };
    const baseAccount = await toSmartContractAccount({
        ...remainingToSmartContractAccountParams,
        transport,
        chain,
        entryPoint,
        accountAddress,
        encodeExecute,
        encodeBatchExecute,
        getNonce,
        ...(entityId === DEFAULT_OWNER_ENTITY_ID
            ? nativeSMASigner(signer, chain, accountAddress, deferredActionData)
            : singleSignerMessageSigner(signer, chain, accountAddress, entityId, deferredActionData)),
    });
    return {
        ...baseAccount,
        getSigner: () => signer,
        signerEntity,
        getExecutionData,
        getValidationData,
        encodeCallData,
    };
}
//# sourceMappingURL=modularAccountV2Base.js.map